import React, {
  useEffect,
  useState,
  useRef,
  ComponentPropsWithoutRef,
} from "react";
import { Button, Image, Stack } from "react-bootstrap";
import { Avatar } from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";

import "./AvatarModal.scss";
import {
  handleUpdateUser,
  handleUploadAvatar,
} from "../../../api-clients/userAccountClient";
import { CustomSpinner } from "../../CustomSpinner/CustomSpinner";
import { showSuccessToast } from "../../../utils/show-toast";
import {
  userCustomAvatarAtom,
  userProfileAtom,
} from "../../../jotai/atoms/user";
import CheckmarkCircleIcon from "../../icons/CheckmarkCircleIcon";
import FixedFooter from "../../FixedFooter/FixedFooter";
import CameraSolidIcon from "../../icons/CameraSolidIcon";
import BottomDrawer from "../../BottomDrawer/BottomDrawer";
import {
  FunctionWithVoidReturn,
  GenericModalProps,
} from "../../../utils/constants/generic-types";
import PhotoIcon from "../../icons/PhotoIcon";
import useDisclosure from "../../../hooks/useDisclosure";

interface UploadPhotoItemProps extends ComponentPropsWithoutRef<"button"> {
  icon: React.ReactNode;
  cta: string;
}

function UploadPhotoItem({ icon, cta, ...props }: UploadPhotoItemProps) {
  return (
    <Stack
      as="button"
      direction="horizontal"
      className="upload-photo-item"
      {...props}
    >
      <span className="icon-container">{icon}</span>
      <span className="cta-text">{cta}</span>
    </Stack>
  );
}

function UploadPhotoDrawer({
  isOpen,
  onClose,
  handleUploadPhoto,
}: GenericModalProps & { handleUploadPhoto: FunctionWithVoidReturn }) {
  const { t } = useTranslation("common", { keyPrefix: "onboarding" });
  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      containerClass="upload-photo-drawer"
      bodyClass="pb-2"
      backdropClassName="upload-photo-drawer-backdrop"
    >
      <Stack gap={4}>
        <h3 className="heading-text">{t("upload_profile_photo")}</h3>
        <Stack className="py-1 ctas-container">
          <UploadPhotoItem
            icon={<CameraSolidIcon />}
            cta={t("take_a_photo")}
            onClick={handleUploadPhoto}
          />
          <UploadPhotoItem
            icon={<PhotoIcon />}
            cta={t("upload_from_gallery")}
            onClick={handleUploadPhoto}
          />
        </Stack>
      </Stack>
    </BottomDrawer>
  );
}

interface AvatarModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  avatars?: Avatar[];
}

function AvatarModal({ isOpen, setIsOpen, avatars }: AvatarModalProps) {
  const { t } = useTranslation("common");
  const profilePicRef = useRef<HTMLInputElement | null>(null);
  const [avatarFiles, setAvatarFiles] = useState<FileList | null>(null);
  const [selectedAvatarId, setSelectedAvatarId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userProfile, setUserProfile] = useAtom(userProfileAtom);
  const [, setUserCustomAvatar] = useAtom(userCustomAvatarAtom);
  const {
    isOpen: isUploadDrawerOpen,
    onOpen: openUploadDrawer,
    onClose: closeUploadDrawer,
  } = useDisclosure();
  const successMessage = t("toasts:profile_photo_updated");

  const handleChooseFile = () => {
    if (profilePicRef.current) {
      profilePicRef.current.click();
    }
  };

  // Save selected avatar from the preset list to user's profile
  useEffect(() => {
    if (!selectedAvatarId) return;

    async function updateAvatar() {
      setIsLoading(true);
      const user = await handleUpdateUser({ avatarId: selectedAvatarId });
      setIsLoading(false);
      if (!user) return;

      showSuccessToast(successMessage);
      setUserProfile(user);
      setIsOpen(false);
    }

    updateAvatar();
  }, [selectedAvatarId, successMessage, setIsOpen, setUserProfile]);

  // Upload custom picture as avatar and save to user's profile
  useEffect(() => {
    if (!avatarFiles) return;

    const uploadAvatar = async () => {
      setIsLoading(true);
      const profile = await handleUploadAvatar(avatarFiles[0]);
      setIsLoading(false);
      setAvatarFiles(null);
      if (!profile) return;

      showSuccessToast(successMessage);
      setUserCustomAvatar(`${profile?.avatar?.url}?${Date.now()}`);
      setUserProfile(profile);
      closeUploadDrawer();
      setIsOpen(false);
    };

    uploadAvatar();
  }, [
    avatarFiles,
    successMessage,
    setIsOpen,
    setUserProfile,
    closeUploadDrawer,
    setUserCustomAvatar,
  ]);

  return (
    <>
      <BottomDrawer
        containerClass="avatar-selector-container"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <Stack gap={4}>
          <h2 className="avatar-selector-heading">
            {t("onboarding.personalise_your_avatar")}
          </h2>
          {isLoading && (
            <div className="d-flex justify-content-center py-2 ">
              <CustomSpinner className="text-white" />
            </div>
          )}

          <div className="avatar-images-container">
            {avatars?.map((avatar, i) => (
              <Button
                key={avatar.id}
                className="bg-transparent border-0 avatar-btn"
                onClick={() => setSelectedAvatarId(avatar.id)}
                aria-label={`select avatar ${i + 1}`}
              >
                <Image
                  src={avatar.url}
                  className="avatar-img"
                  alt="avatar"
                  width={90}
                  height={90}
                />
                {userProfile?.avatar?.id === avatar.id && (
                  <CheckmarkCircleIcon className="avatar-checkmark" />
                )}
              </Button>
            ))}
            <input
              type="file"
              ref={profilePicRef}
              className="d-none"
              onChange={(e) => setAvatarFiles(e.target.files)}
            />
          </div>
        </Stack>
      </BottomDrawer>

      {isOpen && avatars && (
        <FixedFooter className="upload-photo-btn-container">
          <button className="upload-photo-btn" onClick={openUploadDrawer}>
            <CameraSolidIcon />
            {t("onboarding.upload_photo")}
          </button>
        </FixedFooter>
      )}

      <UploadPhotoDrawer
        isOpen={isUploadDrawerOpen}
        onClose={closeUploadDrawer}
        handleUploadPhoto={handleChooseFile}
      />
    </>
  );
}

export default AvatarModal;
